import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import SystemMetrics from './SystemMetrics';
import RecentVisitors from './RecentVisitors';
import PasswordChange from './PasswordChange';
import API from '../services/api';
import '../styles/Dashboard.css';

const Dashboard = () => {
  const { logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [timeRange, setTimeRange] = useState('7');

  const fetchStats = async () => {
    try {
      setError('');
      const data = await API.getStats();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchStats();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (!stats) {
    return <div className="dashboard-error">Failed to load dashboard data</div>;
  }

  // Format visitor timeline data
  const visitorTimelineData = stats.recentVisitors.map(item => {
    const dateStr = item.date;
    const [year, month, day] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      visitors: parseInt(item.count)
    };
  });

  // Format hourly distribution data (0-23 hours)
  const hourlyData = Array.from({ length: 24 }, (_, i) => {
    const hourStat = stats.hourlyDistribution.find(h => parseInt(h.hour) === i);
    return {
      hour: `${i}:00`,
      views: hourStat ? parseInt(hourStat.count) : 0
    };
  });

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <h1>Analytics Dashboard</h1>
        </div>
        <div className="header-right">
          <div className="time-range-selector">
            <button
              className={`range-btn ${timeRange === '7' ? 'active' : ''}`}
              onClick={() => setTimeRange('7')}
            >
              7 Days
            </button>
            <button
              className={`range-btn ${timeRange === '30' ? 'active' : ''}`}
              onClick={() => setTimeRange('30')}
            >
              30 Days
            </button>
            <button
              className={`range-btn ${timeRange === 'all' ? 'active' : ''}`}
              onClick={() => setTimeRange('all')}
            >
              All Time
            </button>
          </div>
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      {error && (
        <div className="error-banner">
          <span>Error: {error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      <nav className="dashboard-nav">
        <button
          className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`nav-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
      </nav>

      <main className="dashboard-content">
        {activeTab === 'overview' && (
          <>
            {/* Top Metric Cards */}
            <div className="metrics-row">
              <div className="metric-card green">
                <div className="metric-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <div className="metric-content">
                  <div className="metric-value">{stats.totalVisitors.toLocaleString()}</div>
                  <div className="metric-label">Total Visitors</div>
                </div>
              </div>

              <div className="metric-card blue">
                <div className="metric-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                </div>
                <div className="metric-content">
                  <div className="metric-value">{stats.totalPageViews.toLocaleString()}</div>
                  <div className="metric-label">Page Views</div>
                </div>
              </div>

              <div className="metric-card orange">
                <div className="metric-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                  </svg>
                </div>
                <div className="metric-content">
                  <div className="metric-value">{stats.todaysViews.toLocaleString()}</div>
                  <div className="metric-label">Today's Views</div>
                </div>
              </div>

              <div className="metric-card yellow">
                <div className="metric-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="8.5" cy="7" r="4"/>
                    <line x1="20" y1="8" x2="20" y2="14"/>
                    <line x1="23" y1="11" x2="17" y2="11"/>
                  </svg>
                </div>
                <div className="metric-content">
                  <div className="metric-value">{stats.newVisitorsPercent}%</div>
                  <div className="metric-label">New Visitors</div>
                </div>
              </div>
            </div>

            {/* Secondary Metric Cards */}
            <div className="metrics-row secondary">
              <div className="metric-card green">
                <div className="metric-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </div>
                <div className="metric-content">
                  <div className="metric-value">
                    {stats.gameStats.player_wins && stats.gameStats.ai_wins
                      ? `${Math.round(
                          (stats.gameStats.player_wins /
                            (parseInt(stats.gameStats.player_wins) + parseInt(stats.gameStats.ai_wins))) *
                            100
                        )}%`
                      : 'N/A'}
                  </div>
                  <div className="metric-label">Player Win Rate</div>
                </div>
              </div>

              <div className="metric-card blue">
                <div className="metric-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
                <div className="metric-content">
                  <div className="metric-value">{stats.gameStats.highest_player_score || 0}</div>
                  <div className="metric-label">Highest Score</div>
                </div>
              </div>

              <div className="metric-card purple">
                <div className="metric-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/>
                    <line x1="7" y1="2" x2="7" y2="22"/>
                    <line x1="17" y1="2" x2="17" y2="22"/>
                    <line x1="2" y1="12" x2="22" y2="12"/>
                    <line x1="2" y1="7" x2="7" y2="7"/>
                    <line x1="2" y1="17" x2="7" y2="17"/>
                    <line x1="17" y1="17" x2="22" y2="17"/>
                    <line x1="17" y1="7" x2="22" y2="7"/>
                  </svg>
                </div>
                <div className="metric-content">
                  <div className="metric-value">{stats.totalGames.toLocaleString()}</div>
                  <div className="metric-label">Games Played</div>
                </div>
              </div>

              <div className="metric-card orange">
                <div className="metric-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
                <div className="metric-content">
                  <div className="metric-value">
                    {stats.gameStats.avg_duration
                      ? `${Math.round(stats.gameStats.avg_duration)}s`
                      : 'N/A'}
                  </div>
                  <div className="metric-label">Avg Game Duration</div>
                </div>
              </div>
            </div>

            {/* Charts Row */}
            <div className="charts-row">
              <div className="chart-card">
                <h3>Visitor Timeline</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={visitorTimelineData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis
                      dataKey="date"
                      stroke="#64748b"
                      style={{ fontSize: '0.85rem' }}
                    />
                    <YAxis stroke="#64748b" style={{ fontSize: '0.85rem' }} />
                    <Tooltip
                      contentStyle={{
                        background: '#2d3250',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        borderRadius: '8px',
                        color: '#e2e8f0'
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="visitors"
                      stroke="#4ade80"
                      strokeWidth={2}
                      dot={{ fill: '#4ade80', r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="chart-card">
                <h3>Hourly Distribution</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis
                      dataKey="hour"
                      stroke="#64748b"
                      style={{ fontSize: '0.75rem' }}
                      interval={3}
                    />
                    <YAxis stroke="#64748b" style={{ fontSize: '0.85rem' }} />
                    <Tooltip
                      contentStyle={{
                        background: '#2d3250',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        borderRadius: '8px',
                        color: '#e2e8f0'
                      }}
                    />
                    <Bar dataKey="views" fill="#4ade80" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* System Metrics */}
            <SystemMetrics />

            {/* Recent Visitors */}
            <RecentVisitors />
          </>
        )}

        {activeTab === 'settings' && <PasswordChange />}
      </main>
    </div>
  );
};

export default Dashboard;
